import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import api from '../../../services/api';

import { useAuth } from '../../../hooks/auth';
import { useToast } from '../../../hooks/toast';
import { useCart } from '../../../hooks/cart';

import { Course as CourseInterface } from '../../../models/CourseModels';

import { Modal } from '../Modal';
import ProgressBar from '../ProgressBar';
import CourseDetailsModal from '../CourseCardV2/Modal/CourseDetailsModal';
import CourseCta, { formatInstallments } from './CourseCta';

import {
  Card,
  Poster,
  PosterButton,
  Scrim,
  Reveal,
  Synopsis,
  BlockedReason,
  ProgressStrip,
  Meta,
  MetaTitle,
  MetaPrice,
} from './styles';

interface CourseCardV3Props {
  course: CourseInterface;
  customClass?: string;
}

/**
 * Card de curso do catálogo.
 *
 * Substitui o par CourseCardV2 / CourseCardMobile por um só componente,
 * responsivo por CSS. O par existia porque os dois carregavam URLs de imagem
 * diferentes (thumburl no desktop, thumburlvertical no mobile), e a troca era
 * feita em JS por useWindowDimensions — o que remontava a árvore inteira a
 * cada resize. <picture> resolve isso no navegador.
 *
 * Nenhuma regra de negócio vive aqui: a árvore de CTA e a fórmula de
 * parcelamento estão em CourseCta.tsx, copiadas do V2 sem alteração.
 */
const CourseCardV3: React.FC<CourseCardV3Props> = ({ course, customClass }) => {
  const { push } = useHistory();
  const { addToast } = useToast();
  const { addCourse, cartCourses } = useCart();
  const { user } = useAuth();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPlayer, setIsPlayer] = useState(false);
  const [courseSeasons, setCourseSeasons] = useState([]);
  const refModal = useRef<any>(null);

  const progress = Math.min(100, Math.round((course.userprogress || 0) * 100));

  const handleOpenCourse = useCallback(() => {
    const canOpen =
      course.comingsoon !== 'true' &&
      !course.blocked &&
      (!course.status_payment || course.status_payment === 'PAID') &&
      (!course.clicksign_is_signed || course.clicksign_is_signed === 'true');

    if (!canOpen) {
      addToast({
        title: course.prerequirementtitle
          ? course.prerequirementtitle
          : course.status_payment && course.status_payment !== 'PAID'
          ? 'Enviamos um e-mail com as informações de pagamento'
          : course.clicksign_is_signed && course.clicksign_is_signed === 'false'
          ? 'Enviamos um e-mail com as informações para assinar o contrato'
          : 'Bloqueado',
        type: 'info',
      });

      /*
       * DESATIVADO a pedido do usuário (17/08).
       *
       * Clicar num curso bloqueado mostrava o toast e, 3s depois, jogava o
       * aluno em /finance. Medido nos dados reais deste tenant: dos 34 cursos
       * bloqueados, TODOS têm `prerequirement` preenchido — o bloqueio é de
       * trilha ("Você deve completar o curso O Evangelho para liberar o
       * acesso"), não de cobrança. Mandar essa pessoa para a tela de faturas
       * responde uma pergunta que ela não fez, e a tira da tela onde estava.
       *
       * O toast acima continua: a mensagem certa ele já dava.
       *
       * Para reativar, basta descomentar as duas linhas.
       */
      // const handleRedirectToUserFinance = () => push('/finance');
      //
      // setTimeout(handleRedirectToUserFinance, 3000);
    } else {
      user !== undefined
        ? push(`/course/${course.courseid}`)
        : push(`/course/gratis/${course.courseid}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course.courseid, user, push, addToast, course.comingsoon]);

  const getSeasonsCourse = useCallback(async (courseid: any) => {
    const response = await api.get<any>(`/course/season?courseid=${courseid}`);
    const structureSeasons: any = [];

    response.data.map((item: any, key: number) => {
      structureSeasons.push({
        key: key,
        value: item.title,
        season: item.seasonid,
      });
      return '';
    });

    setCourseSeasons(structureSeasons);
  }, []);

  const openDetails = useCallback(() => {
    getSeasonsCourse(course.courseid);
    setIsModalOpen(true);
  }, [getSeasonsCourse, course.courseid]);

  const addCourseToCart = useCallback(() => {
    addCourse(course);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addCourse, course]);

  const isCourseInCart = (): boolean => {
    const alreadyInCart = cartCourses.courses.find(
      (i: any) => i.courseid === course.courseid,
    );

    return !!alreadyInCart;
  };

  const handleCloseModalReset = () => {
    setIsModalOpen(false);
    setIsPlayer(false);
  };

  const handlePlayerModal = () => {
    setIsPlayer((prev: boolean) => !prev);
  };

  useEffect(() => {
    const handleCloseModal = (event: MouseEvent) => {
      if (event.target === refModal.current) {
        setIsModalOpen(false);
        setIsPlayer(false);
      }
    };

    document.addEventListener('click', handleCloseModal);

    return () => document.removeEventListener('click', handleCloseModal);
  }, []);

  return (
    <Card className={customClass || ''}>
      <Poster>
        {/* alt vazio de propósito: a capa é decorativa e o nome acessível do
            card vem do botão que a cobre. Antes o alt era a própria URL da
            imagem, que o leitor de tela lia como caminho de CDN.
            Sobre a ausência de <source> para thumburlvertical, ver o
            comentário em styles.ts. */}
        <img
          src={course.thumburl}
          alt=""
          loading="lazy"
          decoding="async"
          width={260}
          height={130}
          /* A capa era background-image, que falha em silêncio. Como <img>,
             uma URL quebrada mostraria o ícone de imagem quebrada — e são 24
             tenants com higiene de asset desconhecida. Escondendo o elemento,
             o pôster fica no preto de palco, como antes. */
          onError={(event) => {
            event.currentTarget.style.display = 'none';
          }}
        />

        <PosterButton
          type="button"
          onClick={openDetails}
          aria-label={`Ver detalhes de ${course.title}`}
        />

        <Scrim aria-hidden="true" />

        <Reveal>
          {/*
            Motivo do bloqueio, no lugar da sinopse.

            `prerequirementtitle` já vinha na resposta da API ("Você deve
            completar o curso O Evangelho para liberar o acesso") mas só era
            mostrado num toast de 3s, no canto oposto da tela ao card que o
            aluno acabou de clicar — some antes de ser lido e não volta.
            Num curso bloqueado, essa frase é a informação mais útil que o
            card tem: sem ela o aluno vê um cadeado e não sabe o que fazer.
            A sinopse cede o lugar porque só uma das duas cabe, e saber o
            caminho vale mais que saber o conteúdo de algo inacessível.
          */}
          {course.blocked && course.prerequirementtitle ? (
            <BlockedReason>{course.prerequirementtitle}</BlockedReason>
          ) : (
            /* Em parte do catálogo `description` é igual ao título, e o título
               já está sempre visível na faixa abaixo — repetir seria ruído. */
            course.description &&
            course.description.trim() !== (course.title || '').trim() && (
              <Synopsis>{course.description}</Synopsis>
            )
          )}
          <CourseCta
            course={course}
            isInCart={isCourseInCart()}
            onOpenCourse={handleOpenCourse}
            onAddToCart={addCourseToCart}
          />
        </Reveal>

        {progress > 0 && (
          <ProgressStrip>
            <ProgressBar at={progress} customHeight={4} />
            <span className="sr-only">{`${progress}% concluído`}</span>
          </ProgressStrip>
        )}
      </Poster>

      <Meta>
        <MetaTitle>{course.title}</MetaTitle>
        {course.comingsoon === 'true' ? (
          <MetaPrice>EM BREVE</MetaPrice>
        ) : (
          !course.is_owner && <MetaPrice>{formatInstallments(course)}</MetaPrice>
        )}
      </Meta>

      <Modal isOpen={isModalOpen}>
        <CourseDetailsModal
          closeModal={() => handleCloseModalReset()}
          setPlayer={() => handlePlayerModal()}
          course={course}
          courseSeasons={courseSeasons}
          openCourseModal={() => handleOpenCourse()}
          addToCart={() => addCourseToCart()}
          alreadyInCart={isCourseInCart()}
          refProp={refModal}
          isPlayer={isPlayer}
        />
      </Modal>
    </Card>
  );
};

export default CourseCardV3;
